import React, { useEffect, useState } from 'react';

import { useLocation } from '@reach/router';

import * as styles from './iterablePreferenceCenter.module.css';
import { PortableText } from '../portableText/portable-text';

export function IterablePreferenceCenter({
  heroText,
  subtitle,
  privacyPolicyText1,
  privacyPolicyText2,
  successHeroText,
  successSubtitle
}) {
  // hooks
  const { search } = useLocation();

  // state
  const [messageTypes, setMessageTypes] = useState([]);
  const [userSelectedMessageTypes, setUserSelectedMessageTypes] = useState([]);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [subscribedMessageTypeIds, setSubscribedMessageTypeIds] = useState([]);
  const [unsubscribedMessageTypeIds, setUnsubscribedMessageTypeIds] = useState([]);
  const [unsubscribedAll, setUnsubscribedAll] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // fetch iterable message types
  useEffect(() => {
    const fetchMessageTypes = async () => {
      const res = await fetch('/.netlify/functions/getIterableMessageTypes');
      const data = await res.json();

      // filter out message types that we don't want to display
      const filteredMessageTypesData = data?.filter(messageType => {
        const { id } = messageType;
        // initially set all message types to unsubscribed
        messageType.subscribed = false;
        // filter out message types that we don't want to display
        return id !== 114605 && id !== 76631 && id !== 76632;
      });

      setMessageTypes(filteredMessageTypesData);
    };
    fetchMessageTypes();
  }, []);

  useEffect(() => {
    // read email from search params
    const params = new URLSearchParams(search);
    const encodedEmail = params.get('email');

    // check if email is base64 encoded
    if (encodedEmail && (/^([a-zA-Z0-9+/]{4})*(([a-zA-Z0-9+/]{2}==)|([a-zA-Z0-9+/]{3}=))?$/g.test(encodedEmail))) {
      // decode email
      const email = atob(encodedEmail);
      setEmail(email);
    } else {
      setEmailError(true);
    }
  }, []);

  // fetch iterable user by email
  useEffect(() => {
    const fetchUserByEmail = async () => {
      const res = await fetch('/.netlify/functions/getIterableUserByEmail', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      if (res.ok === false) {
        return;
      }

      const data = await res.json();
      const userData = data?.user;

      // update message types with user's subscription preferences
      const updatedMessageTypes = messageTypes.map(messageType => {
        const { id } = messageType;
        messageType.subscribed = userData?.dataFields?.subscribedMessageTypeIds.includes(id);
        return messageType;
      });

      setUserSelectedMessageTypes(updatedMessageTypes);
    };

    if (email) {
      fetchUserByEmail();
    }
  }, [email, messageTypes]);

  // handle adding / removing message type ids from associated state
  const setSubscriptionPreferences = (messageType) => {
    // reset the unsubscribed all checkbox
    setUnsubscribedAll(false);

    // prep ui data to select / deselect message type
    const updatedMessageTypes = userSelectedMessageTypes.map(userSelectedMessageType => {
      if (userSelectedMessageType.id === messageType.id) {
        userSelectedMessageType.subscribed = !userSelectedMessageType.subscribed;
      }
      return userSelectedMessageType;
    });

    // add / remove message type id from associated state
    if (messageType.subscribed) {
      setSubscribedMessageTypeIds([...subscribedMessageTypeIds, messageType.id]);
    } else {
      setSubscribedMessageTypeIds(subscribedMessageTypeIds.filter(id => id !== messageType.id));
    }

    setUserSelectedMessageTypes(updatedMessageTypes);
  };

  const deselectAllSubscriptions = () => {
    // reset the unsubscribed message type ids
    // in case someone selects all and then deselects all and submits the form
    if (unsubscribedMessageTypeIds.length) {
      setUnsubscribedAll(false);
      setUnsubscribedMessageTypeIds([]);
      return;
    }

    const updatedMessageTypes = messageTypes.map(messageType => {
      messageType.subscribed = false;
      return messageType;
    });
    // build array of unsubscribed message type ids
    setUnsubscribedAll(!unsubscribedAll);
    setUserSelectedMessageTypes(updatedMessageTypes);
  };

  const updateSubscriptionPreferences = async () => {
    // fetch iterable user by email from netlify function
    const subscribedMessageTypeIds = userSelectedMessageTypes
      .filter(messageType => messageType.subscribed)
      .map(messageType => messageType.id);

    const unsubscribedMessageTypeIds = userSelectedMessageTypes.filter(messageType => !messageType.subscribed).map(messageType => messageType.id);

    const res = await fetch('/.netlify/functions/updateIterableUserSubscriptionPreferences', {
      method: 'POST',
      body: JSON.stringify({
        email,
        unsubscribedMessageTypeIds,
        subscribedMessageTypeIds
      })
    });
    const data = await res.json();

    if (data?.code === 'Success') {
      setShowSuccessMessage(true);
    } else {
      alert('There was an error saving your preferences. Please try again.');
    }
  };

  // handle error where email is not encoded
  if (emailError) {
    return (
      <div className={styles.container}>
        <p>
          The email in the URL is not encoded. Please try again.
        </p>
      </div>
    );
  }

  if (!email || !messageTypes) {
    return (
      <div className={styles.container}>
        <p>
          Loading...
        </p>
      </div>
    );
  }

  if (showSuccessMessage) {
    return (
      <div className={styles.container}>
        <div className={styles.heroContainer}>
          {successHeroText && (
            <h1 className={styles.heroText}>
              {successHeroText}
            </h1>
          )}
          {successSubtitle && (
            <p>
              {successSubtitle}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.heroContainer}>
        {heroText && (
          <h1 className={styles.heroText}>
            {heroText}
          </h1>
        )}
        {subtitle && (
          <p className={styles.subtitle}>{subtitle}</p>
        )}
      </div>
      <div className={styles.optionsContainer}>
        <p className={styles.optionsHeader}>
          Which emails would you like to receive?
        </p>
        <ul className={styles.optionsList}>
          {userSelectedMessageTypes.map(messageType => (
            <li key={messageType.id}>
              <label className={styles.option}>
                <input
                  type='checkbox'
                  checked={messageType.subscribed}
                  onChange={() => setSubscriptionPreferences(messageType)}
                />
                <div>
                  <span className={styles.messageName}>{messageType.name}:</span> {messageType.description}
                </div>
              </label>
            </li>
          ))}
        </ul>
        <hr className={styles.divider} />
        <label className={styles.optionsUnsubscribeAll}>
          <input
            type='checkbox'
            onChange={deselectAllSubscriptions}
            checked={unsubscribedAll}
          />
          Unsubscribe {email} from all emails.
        </label>
      </div>
      <button
        className={styles.brandButton}
        type='button'
        onClick={updateSubscriptionPreferences}
      >
        Save my preferences
      </button>
      <div className={styles.privacyPolicyContainer}>
        {privacyPolicyText1 && (
          <PortableText blocks={privacyPolicyText1} />
        )}
        {privacyPolicyText2 && (
          <PortableText blocks={privacyPolicyText2} />
        )}
      </div>
    </div>
  );
}
